import {
	Button,
	Card,
	cn,
	Dialog,
	IconButton,
	InfoIcon,
	Tooltip,
	XIcon,
} from "@au-schein-de/au-ui-library";
import type { ProductNoEdibleInfo } from "@dransay/api";
import {
	type ProductVendorListItem,
	VENDOR_AGGREGATION_GROUP,
} from "@dransay/api/models";
import { ScrollArea } from "radix-ui";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRouteLoaderData } from "react-router";
import { DismissibleBanner } from "~/components/dismissible-banner.component";
import { useVendorSelection } from "~/contexts/vendor-selection.context";
import type { PriceComparisonDiscount } from "~/features/price-comparison/price-comparison-discounts";
import { useVendorSelectionPriceComparison } from "~/features/price-comparison/use-vendor-selection-price.comparison";
import { mapPdpVendorToPickerItem } from "~/features/products/pharmacy-picker/map-pdp-vendor-to-picker-item";
import {
	isMapDeliveryMethod,
	pharmacyPickerScrollBottomFadeClassName,
	pharmacyPickerScrollbarClassName,
	pharmacyPickerScrollbarThumbClassName,
} from "~/features/products/pharmacy-picker/pharmacy-picker.utils";
import { PharmacyPickerDeliveryMethodChip } from "~/features/products/pharmacy-picker/pharmacy-picker-delivery-method-chip.component";
import { PharmacyPickerList } from "~/features/products/pharmacy-picker/pharmacy-picker-list.component";
import {
	type PharmacyPickerSortContext,
	resolvePharmacyPickerSort,
	sortPharmacyPickerItems,
	usePharmacyPickerSort,
} from "~/features/products/pharmacy-picker/pharmacy-picker-sort-preference";
import { PharmacyPickerStickyHeader } from "~/features/products/pharmacy-picker/pharmacy-picker-sticky-header.component";
import { PharmacyWarningBanner } from "~/features/products/pharmacy-picker/pharmacy-warning-banner.component";
import { usePdpExpressCourierFilter } from "~/features/products/pharmacy-picker/use-pdp-express-courier-filter.hook";
import { usePdpPharmacyRadius } from "~/features/products/pharmacy-picker/use-pdp-pharmacy-radius.hook";
import { useStuckObserver } from "~/features/products/pharmacy-picker/use-stuck-observer.hook";
import { ProductDetailsVendorItem } from "~/features/products/product-details-vendor-item.component";
import {
	createOfferCardDeliveryBadges,
	type ProductDetailsSelectVendorItem,
} from "~/features/products/product-details-vendor-select.utils";
import { ProductDetailsVendorSelectInfoCard } from "~/features/products/product-details-vendor-select-info-card.component";
import {
	type OfferCardAddress,
	type OfferCardViewModel,
	ProductDetailsVendorSelectTrigger,
} from "~/features/products/product-details-vendor-select-trigger.component";
import { usePostalCodeParam } from "~/hooks/use-postal-code-param";
import type { Route } from "../../routes/+types/_storefront";
import { shuffleSamePriceRuns } from "./utils/shuffle-same-price-runs";

export type { ProductDetailsSelectVendorItem };

const PDP_PICKER_HEADER_SHADOW = "shadow-[0_4px_20px_0_rgba(33,42,54,0.1)]";

function pdpVendorDialogPositionClassName(
	enablePharmacyFilters: boolean,
	enableTopPharmacyCartPromotion: boolean,
): string {
	if (enablePharmacyFilters) {
		return "translate-y-[0%] max-md:top-auto max-md:rounded-t-3xl md:translate-y-[-50%] md:top-[50%] md:max-w-[588px]";
	}
	if (enableTopPharmacyCartPromotion) {
		return "translate-y-[0%] md:translate-y-[-50%] top-0 md:top-[50%] md:max-w-[640px]";
	}
	return "translate-y-[0%] md:translate-y-[-50%] top-20 md:top-[50%] max-md:rounded-t-3xl md:max-w-[843px]";
}

function pdpPharmacyPickerHeaderClassName(
	enablePharmacyFilters: boolean,
	isScrolled: boolean,
): string {
	if (enablePharmacyFilters) {
		return cn(
			"sticky top-0 z-10 shrink-0 transition-shadow bg-white",
			isScrolled ? PDP_PICKER_HEADER_SHADOW : "border-b border-gray-200",
		);
	}
	return cn(
		"sticky top-0 z-10 shrink-0 transition-shadow",
		isScrolled ? cn("bg-white", PDP_PICKER_HEADER_SHADOW) : "bg-gray-100",
	);
}

function pdpPharmacyPickerCloseButtonClassName(
	enablePharmacyFilters: boolean,
	isScrolled: boolean,
): string {
	// Figma 4597:58934 "same as mobile": the picker variant keeps the gray
	// circle the mobile sheet close button uses, on desktop too.
	return cn(
		"cursor-pointer rounded-4xl p-2 text-gray-500 !outline-none focus-visible:ring-0",
		enablePharmacyFilters || !isScrolled ? "bg-gray-100" : "bg-white",
	);
}

function pdpPharmacyPickerLayoutClassName(
	enablePharmacyFilters: boolean,
): string {
	return cn(
		"flex min-h-0 flex-col md:h-auto md:max-h-[calc(100dvh-100px)]",
		enablePharmacyFilters ? "h-[85dvh]" : "h-dvh",
	);
}

/**
 * Shipping is the only surface with a Top/non-Top split, so courier and
 * Abholung get neither the Top chip nor the why-top banner. Spreadable, and
 * module-scope, so the component itself gains no branches over it.
 */
const topFilterPropsFor = (
	deliveryMethodKind: string | undefined,
	onTopChange: (selected: boolean) => void,
	description: string,
): { onTopChange?: (selected: boolean) => void; description?: string } =>
	deliveryMethodKind === "shipping" ? { onTopChange, description } : {};

const expressCheckFailedMessageFor = (
	checkFailed: boolean,
	message: string,
): string | undefined => (checkFailed ? message : undefined);

function PdpPharmacyPickerFilterCards({
	enabled,
	sortContext,
	description,
	topSelected,
	onTopChange,
	paySelected,
	onPayChange,
	radiusControl,
	mobileRadiusControl,
	deliveryMethodControl,
	expressSelected,
	onExpressChange,
	expressCheckFailedMessage,
}: {
	enabled: boolean;
	sortContext: PharmacyPickerSortContext;
	description?: string;
	topSelected: boolean;
	onTopChange?: (selected: boolean) => void;
	paySelected: boolean;
	onPayChange: (selected: boolean) => void;
	radiusControl?: ReactNode;
	mobileRadiusControl?: ReactNode;
	deliveryMethodControl?: ReactNode;
	expressSelected?: boolean;
	onExpressChange?: (selected: boolean) => void;
	expressCheckFailedMessage?: ReactNode;
}) {
	if (!enabled) return null;
	return (
		<PharmacyPickerStickyHeader
			isElevated={false}
			embedded
			showSearch={false}
			showFilterCards
			sortContext={sortContext}
			searchValue=""
			searchPlaceholder=""
			clearSearchLabel=""
			onSearchChange={() => undefined}
			onSearchClear={() => undefined}
			onMobileSearchOpen={() => undefined}
			topSelected={topSelected}
			onTopChange={onTopChange}
			paySelected={paySelected}
			onPayChange={onPayChange}
			description={description}
			radiusControl={radiusControl}
			mobileRadiusControl={mobileRadiusControl}
			deliveryMethodControl={deliveryMethodControl}
			expressSelected={expressSelected}
			onExpressChange={onExpressChange}
			expressCheckFailedMessage={expressCheckFailedMessage}
		/>
	);
}

export function ProductDetailsVendorSelect({
	selectedVendor,
	product,
	vendorOptions,
	onVendorSelection,
}: {
	selectedVendor: ProductDetailsSelectVendorItem;
	selectedVendorId?: string;
	product: ProductNoEdibleInfo;
	vendorOptions: ProductDetailsSelectVendorItem[];
	onVendorSelection: (vendor: ProductDetailsSelectVendorItem) => void;
}) {
	const { t, i18n } = useTranslation(["product", "common"]);
	const [vendorModalOpened, toggleVendorModal] = useState(false);
	const [currentSelectedVendor, setCurrentSelectedVendor] =
		useState<ProductDetailsSelectVendorItem>(selectedVendor);
	const [shuffledVendorOptions, setShuffledVendorOptions] =
		useState<ProductDetailsSelectVendorItem[]>(vendorOptions);

	const { getPriceComparisonDiscount } = useVendorSelectionPriceComparison({
		vendorOptions,
	});
	const { selectedDeliveryMethodKind } = useVendorSelection();
	const { postalCode: pickerPostalCode } = usePostalCodeParam();
	const storefrontLoaderData =
		useRouteLoaderData<Route.ComponentProps["loaderData"]>(
			"routes/_storefront",
		);
	const enableTopPharmacyCartPromotion =
		storefrontLoaderData?.enableTopPharmacyCartPromotion ?? false;
	const enablePharmacyFilters =
		storefrontLoaderData?.enablePharmacyFilters ?? false;
	const usePharmacyPicker =
		enablePharmacyFilters || enableTopPharmacyCartPromotion;
	const [pharmacySort] = usePharmacyPickerSort();
	// Figma "Sort by" on PDP: Entfernung for pickup with an address, order value
	// for shipping/courier, price low-to-high always.
	const sortContext: PharmacyPickerSortContext = useMemo(
		() => ({
			surface: "pdp",
			deliveryMethodKind: selectedDeliveryMethodKind,
			hasAddress: Boolean(pickerPostalCode),
		}),
		[selectedDeliveryMethodKind, pickerPostalCode],
	);
	const effectivePharmacySort = resolvePharmacyPickerSort(
		pharmacySort,
		sortContext,
	);
	const [showAll, setShowAll] = useState(!currentSelectedVendor.featured);
	const [topSelected, setTopSelected] = useState(() =>
		Boolean(currentSelectedVendor.featured),
	);
	const [payFilterSelected, setPayFilterSelected] = useState(false);
	const {
		sentinelRef: modalScrollSentinelRef,
		isStuck: isModalHeaderScrolled,
	} = useStuckObserver(vendorModalOpened && usePharmacyPicker);
	const closeModal = () => toggleVendorModal(false);
	/**
	 * A flex child of each branch's own height-budgeted column (not a sibling
	 * outside it) so its height is part of that budget: the scrollable middle
	 * (flex-1 min-h-0) shrinks to leave room for it instead of the footer
	 * silently pushing the dialog past its max-height and getting clipped by
	 * Dialog.Content's overflow-hidden.
	 */
	const selectPharmacyButton = (
		<Button
			onClick={() => {
				closeModal();
				onVendorSelection(currentSelectedVendor);
			}}
			data-testid="select-vendor-pdp"
			className="px-2"
		>
			<span className="whitespace-normal">
				{t("vendor.pdp.vendorSelectionCTA")}
			</span>
		</Button>
	);
	const pickerFooter = (
		<div className="flex w-full shrink-0 flex-col items-stretch justify-center gap-1 max-md:fixed max-md:bottom-0 max-md:left-0 md:relative px-4 md:px-10 py-4 md:py-6 bg-white shadow-[0_4px_20px_0_rgba(33,42,54,0.1)] z-(--z-index-cart-dialog-content)">
			{selectPharmacyButton}
		</div>
	);
	/**
	 * The pre-picker layout sizes itself with its own max-heights and expects the
	 * footer OUTSIDE its padded column: rendered inside it, the bar inherits the
	 * column's left padding (so it stops short of the dialog edge) and its height
	 * is added to a column that is already at its max, so Dialog.Content's
	 * overflow-hidden clips the end of the list -- including the show-non-top
	 * button. Kept verbatim as it shipped, and used whenever that layout renders.
	 */
	const legacyFooter = (
		<div className="min-h-[104px] flex justify-center gap-1 flex-col w-full h-24 md:h-fit fixed md:relative bottom-0 left-0 px-4 md:px-10 bg-white shadow-[0_-4px_20px_0_rgba(33,42,54,0.1)] z-(--z-index-cart-dialog-content)">
			{selectPharmacyButton}
		</div>
	);
	const mappedPharmacies = useMemo(
		() =>
			shuffledVendorOptions.map((vendor) =>
				mapPdpVendorToPickerItem({
					vendor,
					locale: i18n.language,
					formatFreeFromLabel: (amount: string) =>
						t("common:delivery.freeFrom", { amount }),
					discount: getPriceComparisonDiscount(vendor),
				}),
			),
		[shuffledVendorOptions, i18n.language, getPriceComparisonDiscount, t],
	);
	// Figma 602 "Radius selection": pickup only, and only once an address
	// resolves a distance for each pharmacy — otherwise there's nothing to
	// bucket by radius, so the chip is absent rather than disabled.
	const {
		filteredPharmacies: radiusFilteredPharmacies,
		radiusControl,
		mobileRadiusControl,
	} = usePdpPharmacyRadius({
		enabled:
			selectedDeliveryMethodKind === "pickup" && Boolean(pickerPostalCode),
		pharmacies: mappedPharmacies,
		distanceOf: (item) => item.distanceMeters,
	});
	const expressFilter = usePdpExpressCourierFilter({
		deliveryMethodKind: selectedDeliveryMethodKind,
		postalCode: pickerPostalCode,
		pharmacies: radiusFilteredPharmacies,
	});
	const pickerPharmacies = useMemo(() => {
		const expressFiltered = expressFilter.filteredPharmacies;
		const payFiltered = payFilterSelected
			? expressFiltered.filter((item) => item.combinedPaymentEnabled)
			: expressFiltered;
		return sortPharmacyPickerItems(payFiltered, effectivePharmacySort);
	}, [
		expressFilter.filteredPharmacies,
		payFilterSelected,
		effectivePharmacySort,
	]);

	useEffect(() => {
		if (!vendorModalOpened) {
			setCurrentSelectedVendor(selectedVendor);
		}
	}, [vendorModalOpened, selectedVendor]);

	useEffect(() => {
		if (vendorModalOpened) {
			const sortedByPrice = [...vendorOptions].sort(
				(a, b) => a.price - b.price,
			);
			const shuffled = shuffleSamePriceRuns(sortedByPrice);
			setShuffledVendorOptions(shuffled);

			const selectedVendorInOptions = shuffled.find(
				(it) => it.id.toString() === selectedVendor.id.toString(),
			);

			if (selectedVendorInOptions) {
				setCurrentSelectedVendor(selectedVendorInOptions);
				setTopSelected(Boolean(selectedVendorInOptions.featured));
				return;
			}

			const featuredVendors = shuffled.filter((it) => it.featured);
			const vendorToSelect =
				featuredVendors.length > 0 ? featuredVendors[0] : shuffled[0];

			if (vendorToSelect) {
				setCurrentSelectedVendor(vendorToSelect);
				setTopSelected(Boolean(vendorToSelect.featured));
			}
		}
	}, [vendorModalOpened, vendorOptions, selectedVendor]);

	const featuredVendors = shuffledVendorOptions.filter((it) => it.featured);
	const allVendors = shuffledVendorOptions.filter((it) => !it.featured);

	return (
		<div className="w-full flex flex-col gap-3">
			<div className="flex flex-row justify-between items-center px-1">
				<div className="flex gap-1 items-center">
					<span
						className="text-body-bold text-gray-700"
						data-testid="buying-from-label"
					>
						{t("common:buyingFrom")}
					</span>
					<span
						className="inline-flex text-gray-500"
						data-testid="buying-from-info"
					>
						<Tooltip.Info
							info={t("common:buyingFromInfo")}
							iconClassName="text-ansay-700"
						/>
					</span>
				</div>

				{vendorOptions.length > 1 && (
					<Button
						variant="ghost"
						size={null}
						className="text-ansay-700 hover:text-ansay-700 p-0 h-auto text-body-sm-emphasis"
						onClick={() => toggleVendorModal(true)}
						data-testid="see-all-offers"
					>
						{t("common:seeAllOffers", { count: vendorOptions.length })}
					</Button>
				)}
			</div>

			<div className="min-h-[82px] flex">
				<ProductDetailsVendorSelectTrigger
					product={product}
					viewModel={createOfferCardViewModel({
						selectedVendor,
						priceComparisonDiscount: getPriceComparisonDiscount(selectedVendor),
						locale: i18n.language,
						formatFreeFromLabel: (amount) =>
							t("common:delivery.freeFrom", { amount }),
					})}
					toggleVendorModal={toggleVendorModal}
				/>
			</div>
			<Dialog.Root open={vendorModalOpened} onOpenChange={toggleVendorModal}>
				<Dialog.Content
					className={cn(
						"bottom-0 md:bottom-auto overflow-hidden pt-0 px-0 md:p-0 bg-gray-100",
						pdpVendorDialogPositionClassName(
							enablePharmacyFilters,
							enableTopPharmacyCartPromotion,
						),
					)}
					withCloseButton={false}
					onFocusCapture={(event) => event.stopPropagation()}
					aria-describedby={undefined}
					mobileVariant="fullWidth"
				>
					<Dialog.Title className="hidden">
						{t("vendor.pdp.vendorSelectionCTA")}
					</Dialog.Title>
					<Card.Root variant="fullSpace" className="py-0 bg-gray-100">
						{usePharmacyPicker ? (
							<div
								className={pdpPharmacyPickerLayoutClassName(
									enablePharmacyFilters,
								)}
								data-testid="pharmacy-picker-pdp-layout"
							>
								<div
									className={pdpPharmacyPickerHeaderClassName(
										enablePharmacyFilters,
										isModalHeaderScrolled,
									)}
									data-testid="pharmacy-picker-pdp-header"
								>
									<div className="flex justify-between px-4 pb-4 pt-4 md:pl-10 md:pr-9 md:pt-10">
										<div className="flex flex-col gap-1 text-gray-700">
											<div className="text-h4">
												{t("vendor.pdp.vendorSelectionCTA")}
											</div>
										</div>
										<div>
											<IconButton
												icon={<XIcon.Raw />}
												shape="circle"
												color="gray"
												onClick={closeModal}
												className={pdpPharmacyPickerCloseButtonClassName(
													enablePharmacyFilters,
													isModalHeaderScrolled,
												)}
											/>
										</div>
									</div>
									<PdpPharmacyPickerFilterCards
										enabled={enablePharmacyFilters}
										sortContext={sortContext}
										topSelected={topSelected}
										{...topFilterPropsFor(
											selectedDeliveryMethodKind,
											setTopSelected,
											t("vendor.picker.whyTopPharmacyDescription"),
										)}
										paySelected={payFilterSelected}
										onPayChange={setPayFilterSelected}
										radiusControl={radiusControl}
										mobileRadiusControl={mobileRadiusControl}
										deliveryMethodControl={<PharmacyPickerDeliveryMethodChip />}
										{...expressFilter.filterProps}
										expressCheckFailedMessage={expressCheckFailedMessageFor(
											expressFilter.checkFailed,
											t("vendor.picker.express.checkFailed"),
										)}
									/>
								</div>
								<div className="relative flex min-h-0 flex-1 flex-col min-w-0">
									<ScrollArea.Root
										className="relative flex min-h-0 flex-1 flex-col md:max-h-[70dvh] md:min-h-[100px]"
										type="auto"
									>
										<ScrollArea.Viewport className="relative min-h-0 flex-1 overscroll-y-contain [&>div]:!block">
											<div
												ref={modalScrollSentinelRef}
												className="pointer-events-none absolute inset-x-0 top-0 h-px"
												aria-hidden="true"
											/>
											<PharmacyPickerList
												pharmacies={pickerPharmacies}
												selectedId={currentSelectedVendor.id.toString()}
												onSelect={(id) => {
													const vendor = shuffledVendorOptions.find(
														(v) => v.id.toString() === id,
													);
													if (vendor) setCurrentSelectedVendor(vendor);
												}}
												showPrice
												showDistance={
													isMapDeliveryMethod(selectedDeliveryMethodKind) &&
													currentSelectedVendor.distanceMeters != null
												}
												product={product}
												badgeLayout="pdp"
												hideWhyTopCollapsible={enablePharmacyFilters}
												showTopSplit={selectedDeliveryMethodKind === "shipping"}
												activeGroupId={
													topSelected
														? VENDOR_AGGREGATION_GROUP.TOP
														: VENDOR_AGGREGATION_GROUP.ALL
												}
												onSwitchGroup={(groupId) => {
													setTopSelected(
														groupId === VENDOR_AGGREGATION_GROUP.TOP,
													);
												}}
												className="flex flex-col gap-3 px-5 md:px-11 pt-4 pb-28 md:pb-3"
											/>
										</ScrollArea.Viewport>
										<ScrollArea.Scrollbar
											className={pharmacyPickerScrollbarClassName}
											orientation="vertical"
										>
											<ScrollArea.Thumb
												className={pharmacyPickerScrollbarThumbClassName}
											/>
										</ScrollArea.Scrollbar>
									</ScrollArea.Root>
									{/* Anchored to the non-scrolling wrapper's bottom so the fade
									    stays pinned to the visible bottom of the scroll region. */}
									<div
										aria-hidden="true"
										className={pharmacyPickerScrollBottomFadeClassName}
									/>
								</div>
								{pickerFooter}
							</div>
						) : (
							<>
								<div className="flex h-[calc(100dvh-80px)] flex-col gap-4 pb-1 md:h-auto md:max-h-[calc(100dvh-100px)] md:py-10 md:pl-10 md:pb-1">
									<div className="flex justify-between px-4 pt-4 md:pl-0 md:pr-9 md:pt-0">
										<div className="flex flex-col gap-1 text-gray-700">
											<div className="text-h4">
												{t("vendor.pdp.vendorSelectionCTA")}
											</div>
											<div className="flex flex-row gap-1">
												<span className="text-body-sm-emphasis">
													{vendorOptions.length}
												</span>
												<span className="text-body-sm">
													{t("vendor.pdp.vendorSelectionPharmacies")}
												</span>
											</div>
										</div>
										<div>
											<IconButton
												icon={<XIcon.Raw />}
												shape="circle"
												color="gray"
												onClick={closeModal}
												className="cursor-pointer text-gray-500 p-2 bg-white rounded-4xl !outline-none focus-visible:ring-0"
											/>
										</div>
									</div>
									<div className="min-h-0 flex-1 pl-4 pr-1 md:px-0 md:pr-7">
										<div className="flex flex-col gap-6 max-md:h-full md:flex-row md:gap-6">
											<div className="hidden md:block shrink-0">
												<ProductDetailsVendorSelectInfoCard />
											</div>
											<div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overscroll-y-contain pr-3 max-md:pb-28 scrollbar-thin scrollbar-stable md:max-h-[70dvh] md:min-h-[100px] md:pr-2">
												<div className="block md:hidden">
													<ProductDetailsVendorSelectInfoCard />
												</div>
												<div className="mb-2 flex flex-col gap-2">
													{featuredVendors.length === 0 && (
														<PharmacyWarningBanner>
															{t("vendor.pdp.topPharmaciesNoResults")}
														</PharmacyWarningBanner>
													)}
													{featuredVendors.map((vendor, index) => {
														const discount = getPriceComparisonDiscount(vendor);
														return (
															<div
																key={vendor.id}
																className="flex flex-col gap-2"
															>
																<ProductDetailsVendorItem
																	variant={
																		currentSelectedVendor?.id.toString() ===
																		vendor.id.toString()
																			? "selected"
																			: "normal"
																	}
																	priceComparisonDiscount={discount}
																	onSelection={() =>
																		setCurrentSelectedVendor(vendor)
																	}
																	product={product}
																	{...vendor}
																/>
																{index === 0 && discount.visible && (
																	<DismissibleBanner
																		message={t(
																			"vendor.pdp.discountInfoMessage",
																		)}
																		variant="warning"
																		icon={
																			<InfoIcon
																				size={4}
																				colorToken="warning-400"
																			/>
																		}
																		testId="vendor-select-discount-info"
																	/>
																)}
															</div>
														);
													})}
												</div>
												{allVendors.length > 0 && (
													<>
														<div className="flex flex-row justify-between">
															<div className="text-body text-gray-700">
																{t("vendor.pdp.allPharmacies")}{" "}
																<span className="text-gray-500">
																	({allVendors.length})
																</span>
															</div>
															<Button
																variant="ghost"
																size={null}
																className="text-body-sm-emphasis text-ansay-main cursor-pointer"
																onClick={() => setShowAll(!showAll)}
															>
																{showAll ? t("common:hide") : t("common:show")}
															</Button>
														</div>
														<div className="flex flex-col gap-2">
															{showAll &&
																allVendors.map((vendor) => (
																	<ProductDetailsVendorItem
																		key={vendor.id}
																		variant={
																			currentSelectedVendor?.id.toString() ===
																			vendor.id.toString()
																				? "selected"
																				: "normal"
																		}
																		priceComparisonDiscount={getPriceComparisonDiscount(
																			vendor,
																		)}
																		onSelection={() =>
																			setCurrentSelectedVendor(vendor)
																		}
																		product={product}
																		{...vendor}
																	/>
																))}
														</div>
													</>
												)}
											</div>
										</div>
									</div>
								</div>
								{legacyFooter}
							</>
						)}
					</Card.Root>
				</Dialog.Content>
			</Dialog.Root>
		</div>
	);
}

const createOfferCardViewModel = ({
	selectedVendor,
	priceComparisonDiscount,
	locale,
	formatFreeFromLabel,
}: {
	selectedVendor: ProductDetailsSelectVendorItem;
	priceComparisonDiscount: PriceComparisonDiscount;
	locale: string;
	formatFreeFromLabel: (amount: string) => string;
}): OfferCardViewModel => {
	const shouldShowDiscount = priceComparisonDiscount.visible;
	const deliveryBadges = createOfferCardDeliveryBadges({
		deliveryMethodsData: selectedVendor.deliveryMethodsData,
		locale,
		formatFreeFromLabel,
	});

	return {
		pharmacyName: selectedVendor.title,
		price: selectedVendor.price,
		address: createOfferCardAddress({
			formattedAddress: selectedVendor.address,
			addressData: selectedVendor.addressData,
		}),
		distanceLabel: formatDistanceLabel({
			distanceMeters: selectedVendor.distanceMeters,
			locale,
		}),
		deliveryBadges,
		discount: shouldShowDiscount
			? {
					originalPrice: priceComparisonDiscount.nonDiscountedPrice,
					percentage: Math.abs(priceComparisonDiscount.discountPercentage),
				}
			: undefined,
		featured: selectedVendor.featured,
	};
};

const createOfferCardAddress = ({
	formattedAddress,
	addressData,
}: {
	formattedAddress: string | undefined;
	addressData?: ProductVendorListItem["address"];
}): OfferCardAddress | undefined => {
	if (addressData) return createOfferCardAddressFromAddressData(addressData);
	if (formattedAddress)
		return createOfferCardAddressFromFormattedAddress(formattedAddress);
	return undefined;
};

type VendorAddressData = NonNullable<ProductVendorListItem["address"]>;

const createOfferCardAddressFromAddressData = (
	addressData: VendorAddressData,
): OfferCardAddress => {
	const street = [addressData.addressLineOne, addressData.addressLineTwo]
		.filter(Boolean)
		.join(" ")
		.trim();
	return {
		street: street || undefined,
		postalCode: addressData.postalCode ?? undefined,
		city: addressData.city ?? undefined,
	};
};

const createOfferCardAddressFromFormattedAddress = (
	formattedAddress: string,
): OfferCardAddress => {
	const trimmed = formattedAddress.trim();
	if (!trimmed) return {};

	const parsePostalAndCity = (
		value: string,
	): { postalCode: string; city: string } | undefined => {
		const match = value.trim().match(/(\b\d{4,5}\b)\s+(.+)/);
		if (!match) return undefined;
		return { postalCode: match[1], city: match[2].trim() };
	};

	const parts = trimmed
		.split(",")
		.map((it) => it.trim())
		.filter(Boolean);
	if (parts.length >= 2) {
		const street = parts[0];
		const secondPart = parts[1];
		const parsed = parsePostalAndCity(secondPart);
		if (parsed)
			return { street, postalCode: parsed.postalCode, city: parsed.city };
		return { street, city: secondPart };
	}

	const parsedSingleLine = parsePostalAndCity(trimmed);
	if (parsedSingleLine) {
		return {
			postalCode: parsedSingleLine.postalCode,
			city: parsedSingleLine.city,
		};
	}

	return { street: trimmed };
};

const formatDistanceLabel = ({
	distanceMeters,
	locale,
}: {
	distanceMeters?: number | null;
	locale: string;
}): string | undefined => {
	if (distanceMeters === null || distanceMeters === undefined) return undefined;
	if (!Number.isFinite(distanceMeters)) return undefined;
	const km = distanceMeters / 1000;
	const formatted = new Intl.NumberFormat(locale, {
		minimumFractionDigits: 1,
		maximumFractionDigits: 1,
	}).format(km);
	return `~${formatted} km`;
};
